import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import PublicNav from '../components/PublicNav';
import MarketingFooter from '../components/MarketingFooter';
import { Reveal, RevealGroup } from '../components/ui/Reveal';
import { LeaderPortrait } from '../components/team/LeaderPortrait';
import { LEADERS, TEAM_INTRO, CONVICTION } from '../components/team/teamData';
import './AboutPage.css';
import '../components/team/team.css';

const LinkedInIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
  </svg>
);

const jumpTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

function TeamHero() {
  const [tagA, tagB] = TEAM_INTRO.tagline.split('. ');
  return (
    <section className="about-section team-hero" data-testid="team-hero">
      <div className="about-container team-hero-grid">
        <div>
          <Reveal now>
            <nav className="team-crumbs" aria-label="Breadcrumb" data-testid="team-breadcrumb">
              <Link to="/about" data-testid="team-breadcrumb-about">About</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Leadership Team</span>
            </nav>
          </Reveal>
          <Reveal now delay={0.06}>
            <h1 className="about-h1 team-h1" data-testid="team-title">{TEAM_INTRO.title}</h1>
          </Reveal>
          <Reveal now delay={0.12}>
            <p className="team-tagline" data-testid="team-tagline">{tagA}. <em>{tagB}</em></p>
          </Reveal>
          <Reveal now delay={0.18}>
            <p className="about-lead" style={{ maxWidth: 580 }}>{TEAM_INTRO.body}</p>
          </Reveal>
          <Reveal now delay={0.26}>
            <div className="team-jump">
              {LEADERS.map((l) => (
                <button key={l.id} className="team-jump-chip" onClick={() => jumpTo(l.id)} data-testid={`team-jump-${l.id}`}>
                  <LeaderPortrait leader={l} className="team-jump-avatar" eager />
                  <span><strong>{l.name}</strong><small>{l.role}</small></span>
                </button>
              ))}
            </div>
          </Reveal>
        </div>
        <Reveal now delay={0.2}>
          <div className="team-collage" aria-hidden="true">
            {LEADERS.map((l, i) => (
              <div key={l.id} className={`team-collage-card team-collage-${i}`}>
                <LeaderPortrait leader={l} className="team-collage-photo" eager />
                <span className="team-collage-tag">{l.focus}</span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function LeaderProfile({ leader, index }) {
  const flip = index % 2 === 1;
  return (
    <section id={leader.id} className={`about-section team-profile${flip ? ' is-flip' : ''}`} data-testid={`team-profile-${leader.id}`}>
      <div className="about-container team-profile-grid">
        <Reveal className="team-profile-media">
          <div className="team-frame">
            <div className="team-frame-pic"><LeaderPortrait leader={leader} className="team-frame-photo" /></div>
            <div className="team-frame-badge">
              <span>{leader.role}</span>
              <small>{leader.focus}</small>
            </div>
          </div>
        </Reveal>
        <div className="team-profile-body">
          <Reveal>
            <div className="team-index" data-testid={`team-role-${leader.id}`}>{leader.role}</div>
            <h2 className="about-h2 team-name" data-testid={`team-name-${leader.id}`}>{leader.name}</h2>
            <div className="team-focus">{leader.focus}</div>
            <p className="team-headline">{leader.headline}</p>
          </Reveal>
          {leader.bio.map((p) => (
            <Reveal key={p.slice(0, 24)}><p className="about-p team-bio">{p}</p></Reveal>
          ))}
          <Reveal>
            <div className="team-chips" data-testid={`team-expertise-${leader.id}`}>
              {leader.expertise.map((e) => <span key={e} className="about-chip">{e}</span>)}
            </div>
          </Reveal>
          <Reveal>
            <div className="team-mission" data-testid={`team-mission-${leader.id}`}>
              <div className="team-mission-label">Mission</div>
              <p>{leader.mission}</p>
            </div>
          </Reveal>
          {leader.linkedin && (
            <Reveal>
              <a className="team-btn team-btn-linkedin" href={leader.linkedin} target="_blank" rel="noopener noreferrer" data-testid={`team-linkedin-${leader.id}`}>
                {LinkedInIcon} Connect on LinkedIn
              </a>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

function Conviction() {
  const [a, b] = LEADERS;
  return (
    <section className="about-section about-dark" data-testid="team-conviction">
      <div className="grain-overlay" style={{ opacity: 0.18 }} />
      <div className="about-container" style={{ position: 'relative', zIndex: 1 }}>
        <Reveal>
          <div className="about-narrow about-center" style={{ marginBottom: '2.75rem' }}>
            <div className="about-eyebrow">Shared principle</div>
            <h2 className="about-h2">{CONVICTION.title}</h2>
            <p className="about-p" style={{ color: 'rgba(255,255,255,0.72)' }}>{CONVICTION.body}</p>
          </div>
        </Reveal>
        <RevealGroup className="team-equation" stagger={0.12}>
          {[a, b].map((l, i) => (
            <React.Fragment key={l.id}>
              {i === 1 && <Reveal className="team-eq-op">+</Reveal>}
              <Reveal>
                <button className="team-eq-card" onClick={() => jumpTo(l.id)} data-testid={`team-conviction-${l.id}`}>
                  <LeaderPortrait leader={l} className="team-eq-avatar" />
                  <span className="team-eq-name">{l.name}</span>
                  <span className="team-eq-brings">{l.brings}</span>
                </button>
              </Reveal>
            </React.Fragment>
          ))}
          <Reveal className="team-eq-op">=</Reveal>
          <Reveal>
            <div className="team-eq-card is-result">
              <span className="team-eq-name">CHIRE</span>
              <span className="team-eq-brings">{CONVICTION.principle}</span>
            </div>
          </Reveal>
        </RevealGroup>
      </div>
    </section>
  );
}

function TeamCta() {
  const navigate = useNavigate();
  return (
    <section className="about-section" data-testid="team-cta">
      <div className="about-container about-narrow about-center">
        <Reveal>
          <div className="about-eyebrow">Let's talk hiring</div>
          <h2 className="about-h2">Bring your hiring challenge to the people building CHIRE.</h2>
          <p className="about-lead">Whether you're hiring leaders or hiring at scale, we'd love to understand what's slowing your team down.</p>
          <div className="about-cta-row" style={{ justifyContent: 'center', marginTop: '2rem' }}>
            <button className="about-btn about-btn-primary" onClick={() => navigate('/connectwithus')} data-testid="team-cta-talk-btn">Talk to the founders</button>
            <button className="about-btn about-btn-outline" onClick={() => navigate('/about')} data-testid="team-cta-story-btn">Read our story</button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

// Shown at /about/team while TEAM_PAGE_LIVE is off (config/siteFlags.js).
export function TeamPageHold() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="about-page team-page" style={{ background: '#F5FAFC', minHeight: '100vh', overflowX: 'clip' }} data-testid="team-page-hold">
      <PublicNav />
      <section className="about-section team-hero" style={{ minHeight: '56vh', display: 'flex', alignItems: 'center' }}>
        <div className="about-container" style={{ maxWidth: 720 }}>
          <Reveal now>
            <h1 className="about-h1 team-h1" data-testid="team-hold-title">This page is being updated</h1>
          </Reveal>
          <Reveal now delay={0.08}>
            <p className="about-lead" style={{ maxWidth: 560 }} data-testid="team-hold-message">Our Leadership Team page is getting a refresh. It'll be back soon.</p>
          </Reveal>
          <Reveal now delay={0.14}>
            <div className="about-cta-row" style={{ marginTop: '2rem' }}>
              <Link to="/about" className="about-btn about-btn-primary" data-testid="team-hold-about-btn">Read our story</Link>
              <Link to="/connectwithus" className="about-btn about-btn-outline" data-testid="team-hold-contact-btn">Contact us</Link>
            </div>
          </Reveal>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

export default function TeamPage() {
  const { hash } = useLocation();

  useEffect(() => {
    const el = hash && document.getElementById(hash.slice(1));
    if (!el) { window.scrollTo(0, 0); return undefined; }
    const t = setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => clearTimeout(t);
  }, [hash]);

  return (
    <div className="about-page team-page" style={{ background: '#F5FAFC', minHeight: '100vh', overflowX: 'clip' }} data-testid="team-page">
      <PublicNav />
      <TeamHero />
      {LEADERS.map((l, i) => <LeaderProfile key={l.id} leader={l} index={i} />)}
      <Conviction />
      <TeamCta />
      <MarketingFooter />
    </div>
  );
}
